import { useEffect, useState } from "react";
import { levelFromRms } from "./call-support";

export type MicrophoneOption = { id: string; label: string };

const STORAGE_KEY = "polaris:call-microphone";

/** The microphone chosen last time in this browser, if any. */
export function savedMicrophone(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function saveMicrophone(id: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // Private windows: the choice is just not remembered.
  }
}

/**
 * Listens to a microphone while the start dialog is open (ADR 0067): the list of
 * microphones, with their names once Chrome has the permission, and the volume of
 * the chosen one, so a microphone that hears nothing shows before the call.
 */
export function useMicrophonePreview(active: boolean, deviceId: string | null) {
  const [devices, setDevices] = useState<MicrophoneOption[]>([]);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!active) return;
    let stopped = false;
    let stream: MediaStream | null = null;
    let context: AudioContext | null = null;
    let timer: number | undefined;
    setError(null);
    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: deviceId ? { deviceId: { exact: deviceId } } : true,
        });
        if (stopped) return;
        const inputs = (await navigator.mediaDevices.enumerateDevices())
          .filter((device) => device.kind === "audioinput")
          .map((device, index) => ({
            id: device.deviceId,
            label: device.label || `Microfono ${index + 1}`,
          }));
        if (stopped) return;
        setDevices(inputs);
        context = new AudioContext();
        const analyser = context.createAnalyser();
        analyser.fftSize = 2048;
        context.createMediaStreamSource(stream).connect(analyser);
        const samples = new Float32Array(analyser.fftSize);
        timer = window.setInterval(() => {
          analyser.getFloatTimeDomainData(samples);
          let sum = 0;
          for (const sample of samples) sum += sample * sample;
          setLevel(levelFromRms(Math.sqrt(sum / samples.length)));
        }, 100);
      } catch (cause) {
        if (stopped) return;
        const name =
          typeof cause === "object" && cause !== null && "name" in cause ? String(cause.name) : "";
        setError(
          name === "NotAllowedError"
            ? "Chrome non ha il permesso del microfono: consentilo dall'icona a sinistra dell'indirizzo."
            : name === "OverconstrainedError" || name === "NotFoundError"
              ? "Questo microfono non è collegato: scegline un altro."
              : "Non riusciamo ad ascoltare il microfono.",
        );
      }
    })();
    return () => {
      stopped = true;
      window.clearInterval(timer);
      for (const track of stream?.getTracks() ?? []) track.stop();
      void context?.close().catch(() => undefined);
      setLevel(0);
    };
  }, [active, deviceId]);

  return { devices, level, error };
}
